import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult
} from '@tanstack/react-query';
import { apiClient } from '../client';
import { invalidateEntity } from '../cache-invalidation';
import { recordingKeys } from '../query-keys';
import {
  editProcessStepRecordingContract,
  stopProcessStepRecordingContract,
  finalizeProcessStepRecordingContract,
  resumeProcessStepRecordingContract,
  recordStepRecordingActivityContract,
  generateStepRecordingSummaryContract,
  reassignRecordingContract,
  type EditProcessStepRecordingRequest,
  type EditProcessStepRecordingResponse,
  type StopProcessStepRecordingResponse,
  type FinalizeProcessStepRecordingRequest,
  type FinalizeProcessStepRecordingResponse,
  type ResumeProcessStepRecordingResponse,
  type GenerateStepRecordingSummaryResponse,
  type ReassignRecordingRequest,
  type ShowProcessStepRecordingResponse,
  type ProcessStepRecording
} from '@bliks-io/contracts';

/**
 * Per-recording write sequence, scoped to the QueryClient so tests with fresh
 * clients don't leak state. Each update mutation takes the next number at
 * onMutate; only the mutation holding the LATEST number may write its server
 * response or roll back. An `isMutating === 1` guard wouldn't cover responses
 * that complete fully out of order (old response landing after the newer
 * mutation already settled) — the sequence does.
 */
const updateSeqByClient = new WeakMap<QueryClient, Map<string, number>>();

function takeUpdateSeq(queryClient: QueryClient, recordingId: string): number {
  let seqByRecording = updateSeqByClient.get(queryClient);
  if (!seqByRecording) {
    seqByRecording = new Map();
    updateSeqByClient.set(queryClient, seqByRecording);
  }
  const seq = (seqByRecording.get(recordingId) ?? 0) + 1;
  seqByRecording.set(recordingId, seq);
  return seq;
}

function isLatestUpdate(queryClient: QueryClient, recordingId: string, seq: number): boolean {
  return updateSeqByClient.get(queryClient)?.get(recordingId) === seq;
}

/**
 * Update step recording mutation
 *
 * Updates an existing process step recording.
 * - Uses optimistic updates for immediate UI feedback
 * - Invalidates step recording detail cache on success
 * - Rolls back on error
 * - Concurrent writes (debounced text flush racing a toggle/successor PATCH)
 *   follow the last-writer-wins pattern: only the latest-issued mutation
 *   writes the server response / rolls back, so a stale response can't
 *   clobber a newer optimistic value.
 */
export function useUpdateStepRecording() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recordingId,
      data
    }: {
      recordingId: string;
      data: EditProcessStepRecordingRequest;
    }): Promise<EditProcessStepRecordingResponse> => {
      const path = editProcessStepRecordingContract.path.replace(':id', recordingId);
      const { data: response } = await apiClient.patch<EditProcessStepRecordingResponse>(
        path,
        data
      );
      return response;
    },
    onMutate: async ({ recordingId, data }) => {
      // Taken before any await so the sequence reflects mutate-call order.
      const seq = takeUpdateSeq(queryClient, recordingId);

      // Cancel any outgoing refetches to avoid them overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: recordingKeys.stepRecording(recordingId) });

      // Snapshot the previous value
      const previousRecording = queryClient.getQueryData(recordingKeys.stepRecording(recordingId));

      // Optimistically update the cache with deep merge for nested objects
      queryClient.setQueryData(
        recordingKeys.stepRecording(recordingId),
        (old: ShowProcessStepRecordingResponse | undefined) => {
          if (!old || !('processStepRecording' in old)) return old;

          // Deep merge nested objects
          // Using any here because we're dynamically merging partial data
          const merged: Record<string, unknown> = { ...old.processStepRecording };
          (Object.keys(data) as Array<keyof EditProcessStepRecordingRequest>).forEach((key) => {
            const value = data[key];
            if (value && typeof value === 'object' && !Array.isArray(value)) {
              // For nested objects, merge with existing nested object
              const existingValue = merged[key];
              merged[key] = {
                ...(existingValue && typeof existingValue === 'object' ? existingValue : {}),
                ...value
              };
            } else {
              // For primitives and arrays, replace directly
              merged[key] = value;
            }
          });

          return {
            processStepRecording: merged as ProcessStepRecording
          };
        }
      );

      // Return context with the snapshot for rollback and this write's sequence
      return { previousRecording, seq };
    },
    onError: (_err, { recordingId }, context) => {
      // Roll back only as the latest writer — a newer mutation's optimistic
      // value owns the cache and its own settlement will reconcile.
      if (context?.previousRecording && isLatestUpdate(queryClient, recordingId, context.seq)) {
        queryClient.setQueryData(
          recordingKeys.stepRecording(recordingId),
          context.previousRecording
        );
      }
    },
    onSuccess: (response, variables, context) => {
      // Write the server response only as the latest writer; a stale response
      // landing late would erase newer optimistic fields, and with
      // refetchType 'none' nothing would ever repair that.
      if (isLatestUpdate(queryClient, variables.recordingId, context.seq)) {
        queryClient.setQueryData(recordingKeys.stepRecording(variables.recordingId), {
          processStepRecording: response.processStepRecording || response
        });
      }

      // Also invalidate to trigger any dependent queries or subscriptions
      queryClient.invalidateQueries({
        queryKey: recordingKeys.stepRecording(variables.recordingId),
        refetchType: 'none' // Don't refetch since we just updated the cache
      });
    }
  });
}

/**
 * Factory for the recording state-machine actions (stop, resume,
 * generate-summary). Each is a POST against `<contract.path>/:id` with no
 * body. Stop and resume change `status`, which feeds list-tasks, the process
 * map, and the recording dashboard — so all three invalidate the full
 * `recording` entity, matching finalize and reassign. Generate-summary
 * only touches the recording's own fields, but uses the same invalidation
 * for symmetry (cheap, and avoids a maintenance trap if generated fields
 * later get denormalized elsewhere).
 *
 * The invalidation is returned, so `isPending` lasts until the refetched
 * recording lands: the step-recording page opens and closes the review dialog
 * from that refetch, and its buttons stay busy until then.
 *
 * Finalize is intentionally NOT built from this factory: it takes a request
 * body, so its mutation signature differs.
 *
 * Pass `invalidateOnSuccess: false` for fire-and-forget pings (the heartbeat)
 * that must NOT churn the cache — otherwise a ~20s timer would refetch the
 * recording on every beat.
 */
function useRecordingActionMutation<T>(
  contract: { path: string },
  { invalidateOnSuccess = true }: { invalidateOnSuccess?: boolean } = {}
): UseMutationResult<T, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (recordingId: string): Promise<T> => {
      const path = contract.path.replace(':id', recordingId);
      const { data } = await apiClient.post<T>(path, {});
      return data;
    },
    onSuccess: invalidateOnSuccess ? () => invalidateEntity(queryClient, 'recording') : undefined
  });
}

/**
 * Stop recording mutation
 *
 * Transitions the recording from in_progress → pending_finalization so the
 * review dialog opens. The dialog itself drives AI generation by calling
 * `useGenerateStepRecordingSummary` — stop no longer triggers any AI work.
 */
export function useStopRecording() {
  return useRecordingActionMutation<StopProcessStepRecordingResponse>(
    stopProcessStepRecordingContract
  );
}

/**
 * Finalize recording mutation
 *
 * Transitions pending_finalization → finalized with the user-approved
 * name/description. Returns the created pending survey so the page can
 * navigate to /my-tasks?surveyId=… for the feedback flow.
 */
export function useFinalizeRecording() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recordingId,
      body
    }: {
      recordingId: string;
      body: FinalizeProcessStepRecordingRequest;
    }): Promise<FinalizeProcessStepRecordingResponse> => {
      const path = finalizeProcessStepRecordingContract.path.replace(':id', recordingId);
      const { data } = await apiClient.post<FinalizeProcessStepRecordingResponse>(path, body);
      return data;
    },
    onSuccess: () => {
      invalidateEntity(queryClient, 'recording');
    }
  });
}

/**
 * Resume recording mutation
 *
 * Returns a stopped recording to in_progress so the user can edit again
 * before stopping a second time. AI-generated fields are preserved.
 */
export function useResumeRecording() {
  return useRecordingActionMutation<ResumeProcessStepRecordingResponse>(
    resumeProcessStepRecordingContract
  );
}

/**
 * Record activity (heartbeat) mutation
 *
 * Fire-and-forget ping the editor sends on a timer while a recording is
 * in_progress. The server measures the idle-capped gap since the previous beat
 * and folds it into active editing time; the response is empty (204). Skips
 * cache invalidation on purpose — at ~20s intervals, refetching the recording
 * on every beat would be wasteful and could clobber unsaved local edits.
 */
export function useRecordHeartbeat() {
  return useRecordingActionMutation<undefined>(recordStepRecordingActivityContract, {
    invalidateOnSuccess: false
  });
}

/**
 * Generate step recording summary mutation
 *
 * Synchronously calls the AI to produce a name and description for a
 * `pending_finalization` recording, persists the result on the server, and
 * returns the updated recording. Used by the review dialog for both the
 * initial auto-fire on open (when `generatedName` is absent) and the
 * explicit regenerate button.
 */
export function useGenerateStepRecordingSummary() {
  return useRecordingActionMutation<GenerateStepRecordingSummaryResponse>(
    generateStepRecordingSummaryContract
  );
}

/**
 * Reassign recording mutation
 *
 * Reassigns a process step recording to another user or marks it as unclear.
 * - Invalidates cross-entity dependents of `recording` so the original
 *   assignee's My Activities and the new assignee's Tasks both refresh.
 */
export function useReassignRecording() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recordingId,
      data
    }: {
      recordingId: string;
      data: ReassignRecordingRequest;
    }): Promise<void> => {
      const path = reassignRecordingContract.path.replace(':id', recordingId);
      await apiClient.post(path, data);
    },
    onSuccess: () => {
      invalidateEntity(queryClient, 'recording');
    }
  });
}
